import Phaser from "phaser";
import LilyColumn from "./LilyColumn";
import { shuffle } from "@educaplay/core";

// Constants
const DURATIONS = {
    JUMP: 750,
    ANSWER_CASCADE: 200,
    SINKING: 1500,
    HOVER_SCALE: 100,
    VICTORY_JUMP: 750,
};

const SCALES = {
    PLATFORM_BASE: 0.1953125,
    FROG_BASE: 0.53511705685,
};

const OFFSETS = {
    X: 215,
    SHADOW: 10,
};

const BANK = {
    VISIBLE_OFFSET_Y: 5,
    HIDDEN_OFFSET_Y: 22,
};

const LAYOUT = {
    FROG_PAD_BOTTOM_MARGIN: {
        LANDSCAPE: 16,
        PORTRAIT: 48,
    },
};

const FONTS = {
    UI: 'Nunito, "Segoe UI", Segoe, Helvetica, Arial, sans-serif',
};

const BADGE = {
    FILL: 0x69bb1d,
    SHADOW: 0x070e00,
};

const PAD_BASE_SIZE = 200;
const SCALE_TUNING = 0.95;

const PAD_SPACING = 230; // Vertical distance between pads in a column (base units)
const PAD_COUNT = 6; // Pads per column (covers main + extension area)
const SIDE_COLUMNS = 2; // Extra deco columns on each side beyond A/B/C

/**
 * Compute the base Y (head position) for a column at a given distance from center.
 * dist=0 (B): frogY - padSpacing (one full spacing up)
 * |dist|>=1:  frogY - |dist| * padSpacing / 2 (inverted triangle / brick pattern)
 *
 * This produces the brick-wall zigzag:
 *   dist=±1 (A/C): frogY - ps/2
 *   dist=±2:       frogY - ps   (same as B)
 *   dist=±3:       frogY - 1.5ps
 */
function getColumnBaseY(frogY, dist, padSpacing) {
    if (dist === 0) return frogY - padSpacing;
    return frogY - Math.abs(dist) * padSpacing / 2;
}

export default class GameScene extends Phaser.Scene {
    constructor() {
        super({ key: "GameScene" });

        // Game state
        this.moving = false;
        this.finish = false;
        this.currentQuestionIndex = 0;
        this.scalePlatform = 1;
        this.lastHeight = undefined;

        // Callbacks
        this.onAnswer = null;
        this.onAnimatingChange = null;
        this.onBankControl = null;
        this.soundEnabled = true;

        // Column-based layout
        this.frogPad = null; // { sprite, shadow }
        this.columns = null; // { A, B, C } shortcut refs
        this.decoMap = new Map(); // absolute xMul → LilyColumn (deco columns)
        this.centerXMul = 0; // absolute xMul where the frog (column B) is
        this._pendingNearDeco = null; // { xMul, col } pre-created before lateral jump

        // Shore / finish line
        this.sueloFinal = null;
        this._correctKey = null;

        // Game-over flag (set externally by framework when lives reach 0)
        this._markedGameOver = false;
        this._onGameOverComplete = null;
    }

    init(data) {
        this.questions = data.questions || [];
        this.onAnswer = data.onAnswer;
        this.onAnimatingChange = data.onAnimatingChange;
        this.onQuestionStart = data.onQuestionStart;
        this.onBankControl = data.onBankControl;
        this.soundEnabled = data.soundEnabled !== false;
        this.randomizeAnswers = data.randomizeAnswers !== false;

        // Reset state
        this.moving = false;
        this.finish = false;
        this.currentQuestionIndex = 0;
        this.lastHeight = undefined;
        this.sueloFinal = null;
        this._correctKey = null;
        this._markedGameOver = false;
        this._onGameOverComplete = null;
        this.currentCorrectAnswers = [];

        this.answerOrder = {};
    }

    create() {
        this.setAnimating(true);
        this.setBankControl({ visible: false, offsetY: BANK.HIDDEN_OFFSET_Y });

        this.createBackground();
        this.setupAudio();
        this.setupKeyboard();
        this.createGameElements();
        this.setupResizeHandler();
        this.startQuestion();
    }

    // ─── Background ──────────────────────────────────────────────────

    createBackground() {
        this.background = null;
    }

    // ─── Audio ───────────────────────────────────────────────────────

    setupAudio() {
        if (this.soundEnabled) {
            this.sounds = {
                salto: this.sound.add("saltoAudio"),
                over: this.sound.add("overAudio"),
                croar: this.sound.add("croarAudio"),
                pressOk: this.sound.add("pressOkAudio"),
                pressWrong: this.sound.add("pressWrongAudio"),
                saltoOk: this.sound.add("saltoContactOkAudio"),
                saltoWrong: this.sound.add("saltoContactWrongAudio"),
                hundimiento: this.sound.add("hundimientoContactAudio"),
                burbujas: this.sound.add("burbujasAudio"),
                error: this.sound.add("errorAudio"),
                coin: this.sound.add("coin"),
                gameWin: this.sound.add("game_Win"),
                gameOver: this.sound.add("game_Over"),
            };
        }
    }

    // ─── Keyboard ────────────────────────────────────────────────────

    setupKeyboard() {
        this.keys = this.input.keyboard.addKeys("A,B,C,LEFT,UP,RIGHT");

        this.keys.A.on("up", () => this.handleAnswer("A"));
        this.keys.LEFT.on("up", () => this.handleAnswer("A"));
        this.keys.B.on("up", () => this.handleAnswer("B"));
        this.keys.UP.on("up", () => this.handleAnswer("B"));
        this.keys.C.on("up", () => this.handleAnswer("C"));
        this.keys.RIGHT.on("up", () => this.handleAnswer("C"));
    }

    // ─── Game elements ───────────────────────────────────────────────

    createGameElements() {
        // Guard: ensure canvas has valid dimensions before creating sprites
        if (this.scale.width <= 0 || this.scale.height <= 0) {
            return;
        }

        const centerX = this.scale.width / 2;
        const frogY = this.getFrogBlockY(this.scale.width, this.scale.height, 1);

        // Frog sprite
        this.rana = this.add
            .sprite(centerX, frogY, "rana")
            .setScale(SCALES.FROG_BASE)
            .setDepth(100)
            .setOrigin(0.5, 0.38461538461);

        // Frog pad (standalone, below column B)
        this.frogPad = this._createPad(centerX, frogY, 1);

        // Three main columns
        this.centerXMul = 0;
        this.columns = {
            A: LilyColumn.create(this, centerX - OFFSETS.X, frogY - PAD_SPACING / 2, PAD_SPACING, PAD_COUNT, 1),
            B: LilyColumn.create(this, centerX, frogY - PAD_SPACING, PAD_SPACING, PAD_COUNT, 1),
            C: LilyColumn.create(this, centerX + OFFSETS.X, frogY - PAD_SPACING / 2, PAD_SPACING, PAD_COUNT, 1),
        };

        // Decorative columns
        this._createDecoColumns(centerX, frogY);

        // Camera follows frog
        this.cameras.main.startFollow(this.rana);
        this.cameras.main.setFollowOffset(0, this.getCameraFollowOffsetY(this.scale.width, this.scale.height, 1));

        // Answer text objects
        this.createAnswerTexts();

        // Initial resize
        this.resize();
    }

    // ─── Pad helpers ─────────────────────────────────────────────────

    _createPad(x, y, scale) {
        const shadowOff = OFFSETS.SHADOW * scale;
        const shadow = this.add
            .image(x, y + shadowOff, "platform-shadow")
            .setScale(scale);
        const sprite = this.add
            .sprite(x, y, "platform")
            .setScale(SCALES.PLATFORM_BASE * scale);
        return { sprite, shadow };
    }

    _destroyPad(pad) {
        if (!pad) return;
        if (pad.sprite) {
            this.tweens.killTweensOf(pad.sprite);
            if (pad.sprite.anims && pad.sprite.anims.isPlaying) {
                // Dejar que la animación termine antes de destruir el sprite
                const sprite = pad.sprite;
                sprite.once("animationcomplete", () => sprite.destroy());
            } else {
                if (pad.sprite.anims) pad.sprite.anims.stop();
                pad.sprite.destroy();
            }
        }
        if (pad.shadow) {
            this.tweens.killTweensOf(pad.shadow);
            pad.shadow.destroy();
        }
    }

    _sinkPad(pad, animKey = "hundimiento_platform", scale) {
        if (!pad) return;
        pad.shadow.setAlpha(0);
        if (pad.sprite && pad.sprite.anims) {
            if (scale !== undefined) pad.sprite.setScale(scale);
            pad.sprite.anims.play(animKey);
            pad.sprite.once("animationcomplete", () => {
                pad.sprite.setAlpha(0);
            });
        }
    }

    /**
     * FIFO a column: remove old head, add new tail (maintains pad count).
     */
    _fifoColumn(col, padSpacing, scale) {
        const head = col.dequeueHead();
        if (head) this._destroyPad(head);
        col.enqueueTail(padSpacing, scale);
    }

    // ─── Decorative columns ──────────────────────────────────────────

    /**
     * Create deco columns in the decoMap (dist ±2, ±3 from center).
     * @param {number} frogX - Reference X (frog / frogPad position)
     * @param {number} frogY - Reference Y (frog / frogPad position)
     */
    _createDecoColumns(frogX, frogY) {
        const scale = this.scalePlatform || 1;
        const offsetX = OFFSETS.X * scale;
        const padSpacing = PAD_SPACING * scale;
        const center = this.centerXMul;

        this.decoMap = new Map();
        for (let d = -SIDE_COLUMNS - 1; d <= SIDE_COLUMNS + 1; d++) {
            if (d >= -1 && d <= 1) continue; // skip A/B/C positions
            const xMul = center + d;
            const cx = frogX + d * offsetX;
            const cy = getColumnBaseY(frogY, d, padSpacing);
            this.decoMap.set(xMul, LilyColumn.create(this, cx, cy, padSpacing, PAD_COUNT, scale));
        }
    }

    _destroyDecoColumns() {
        for (const col of this.decoMap.values()) {
            col.destroy();
        }
        this.decoMap = new Map();
    }

    _sinkDecoHeads(scale, count = 1) {
        for (const col of this.decoMap.values()) {
            col.sinkHeads(count, "hundimiento_platform", scale);
        }
    }

    /**
     * Pre-create the near-edge deco column that will become visible when the
     * camera pans during a lateral jump.  Stored OUTSIDE decoMap so it won't
     * be FIFOed during _advanceLateral.  Adopted into decoMap in step 8.
     */
    _preCreateNearEdgeDeco(key) {
        const scale = this.scalePlatform;
        const dir = key === "A" ? -1 : 1;
        const futureCenter = this.centerXMul + dir;
        const nearXMul = key === "A"
            ? futureCenter - SIDE_COLUMNS - 1
            : futureCenter + SIDE_COLUMNS + 1;

        // Don't recreate if it's already pending or already in decoMap
        if (this._pendingNearDeco?.xMul === nearXMul) return;
        if (this.decoMap.has(nearXMul)) return;

        const offsetX = OFFSETS.X * scale;
        const padSpacing = PAD_SPACING * scale;
        const nearDist = nearXMul - futureCenter;
        // Reference: chosen column's head (where the frog will land / future frogPad)
        const chosen = key === "A" ? this.columns.A : this.columns.C;
        const refX = chosen.headX;
        const refY = chosen.headY;

        this._pendingNearDeco = {
            xMul: nearXMul,
            col: LilyColumn.create(
                this,
                refX + nearDist * offsetX,
                getColumnBaseY(refY, nearDist, padSpacing),
                padSpacing, PAD_COUNT, scale,
            ),
        };
    }

    _destroyPendingNearDeco() {
        if (this._pendingNearDeco) {
            this._pendingNearDeco.col.destroy();
            this._pendingNearDeco = null;
        }
    }

    // ─── Shore (finish line) ─────────────────────────────────────────

    /**
     * Create the shore sprite above the correct answer's column.
     * Slides down from off-screen with a tween.
     */
    _createShore() {
        this._destroyShore();

        // Find the correct answer key (A, B, or C)
        this._correctKey = Object.keys(this.currentAnswers).find(
            (k) => {
                const answer = this.currentAnswers[k];
                return answer && this.currentCorrectAnswers.includes(answer.id);
            },
        );
        if (!this._correctKey) return;

        const col = this.columns[this._correctKey];
        const scale = this.scalePlatform;
        const finalX = col.headX;
        const finalY = col.headY - this.lastHeight - 25;

        this.sueloFinal = this.add
            .sprite(finalX, finalY - 1000, "suelo")
            .setDepth(5)
            .setScale(scale)
            .setOrigin(0.5, 1);

        this.tweens.add({
            targets: this.sueloFinal,
            y: finalY,
            duration: 1000,
        });
    }

    /**
     * Place the shore at a given position without animation (used on respawn).
     */
    _createShoreAt(x, y) {
        this._destroyShore();
        const scale = this.scalePlatform;
        this.sueloFinal = this.add
            .sprite(x, y - this.lastHeight - 25, "suelo")
            .setDepth(5)
            .setScale(scale)
            .setOrigin(0.5, 1);
    }

    _destroyShore() {
        if (this.sueloFinal) {
            this.tweens.killTweensOf(this.sueloFinal);
            this.sueloFinal.destroy();
            this.sueloFinal = null;
        }
        this._correctKey = null;
    }

    // ─── Answer texts ────────────────────────────────────────────────

    createAnswerTexts() {
        const textStyle = {
            fontSize: "28px",
            fontFamily: FONTS.UI,
            color: "#ffffff",
            align: "center",
            fontStyle: "bold",
            fontWeight: 800,
            lineSpacing: 0,
            wordWrap: {
                width: 150,
                useAdvancedWrap: true,
            },
            stroke: "#ffffff",
            strokeThickness: 1,
        };

        const labelStyle = {
            fontSize: "20px",
            fontFamily: FONTS.UI,
            color: "#ffffff",
            fontStyle: "bold",
            fontWeight: 800,
        };

        this.textA = this.add
            .text(this.columns.A.headX, this.columns.A.headY - 10, "", textStyle)
            .setOrigin(0.5)
            .setDepth(150)
            .setAlpha(0);
        this.labelShadowA = this.add
            .circle(this.columns.A.headX, this.columns.A.headY - 30 + 2, 18, BADGE.SHADOW, 0.8)
            .setDepth(158)
            .setAlpha(0);
        this.labelBgA = this.add
            .circle(this.columns.A.headX, this.columns.A.headY - 30, 18, BADGE.FILL)
            .setDepth(159)
            .setAlpha(0);
        this.labelA = this.add
            .text(this.columns.A.headX, this.columns.A.headY - 30, "A", labelStyle)
            .setOrigin(0.5)
            .setDepth(160)
            .setAlpha(0);

        this.textB = this.add
            .text(this.columns.B.headX, this.columns.B.headY - 10, "", textStyle)
            .setOrigin(0.5)
            .setDepth(150)
            .setAlpha(0);
        this.labelShadowB = this.add
            .circle(this.columns.B.headX, this.columns.B.headY - 30 + 2, 18, BADGE.SHADOW, 0.8)
            .setDepth(158)
            .setAlpha(0);
        this.labelBgB = this.add
            .circle(this.columns.B.headX, this.columns.B.headY - 30, 18, BADGE.FILL)
            .setDepth(159)
            .setAlpha(0);
        this.labelB = this.add
            .text(this.columns.B.headX, this.columns.B.headY - 30, "B", labelStyle)
            .setOrigin(0.5)
            .setDepth(160)
            .setAlpha(0);

        this.textC = this.add
            .text(this.columns.C.headX, this.columns.C.headY - 10, "", textStyle)
            .setOrigin(0.5)
            .setDepth(150)
            .setAlpha(0);
        this.labelShadowC = this.add
            .circle(this.columns.C.headX, this.columns.C.headY - 30 + 2, 18, BADGE.SHADOW, 0.8)
            .setDepth(158)
            .setAlpha(0);
        this.labelBgC = this.add
            .circle(this.columns.C.headX, this.columns.C.headY - 30, 18, BADGE.FILL)
            .setDepth(159)
            .setAlpha(0);
        this.labelC = this.add
            .text(this.columns.C.headX, this.columns.C.headY - 30, "C", labelStyle)
            .setOrigin(0.5)
            .setDepth(160)
            .setAlpha(0);

        for (const key of ["A", "B", "C"]) {
            const answerText = this[`text${key}`];
            const labelText = this[`label${key}`];
            answerText.setShadow(0, 2, "#070e00", 0, false, true);
            labelText.setShadow(0, 1, "#070e00", 0, false, true);
        }
    }

    // ─── Resize ──────────────────────────────────────────────────────

    setupResizeHandler() {
        this.scale.on("resize", () => {
            this.resize();
        });
    }

    resize() {
        const width = this.scale.width;
        const height = this.scale.height;

        if (width <= 0 || height <= 0) return;

        // If game elements haven't been created yet (due to 0 dimensions on first create()),
        // create them now that dimensions are valid
        if (!this.rana) {
            this.createGameElements();
            return;
        }

        if (this.background) {
            this.background.setPosition(width / 2, height / 2);
            this.background.setSize(width, height);
        }

        const scalePlatform = this.getPlatformScale(width, height);

        this.lastHeight = PAD_BASE_SIZE * scalePlatform;
        this.scalePlatform = scalePlatform;

        if (this.cameras && this.cameras.main) {
            this.cameras.main.setFollowOffset(0, this.getCameraFollowOffsetY(width, height, scalePlatform));
        }

        this.applyScales(scalePlatform);
    }

    getPlatformScale(width, height) {
        if (height > width) {
            return ((width * 0.3125) / PAD_BASE_SIZE) * SCALE_TUNING;
        }
        return ((height * 0.35) / PAD_BASE_SIZE) * SCALE_TUNING;
    }

    getFrogBlockY(width, height, scale) {
        const padHeight = PAD_BASE_SIZE * scale;
        const isPortrait = height > width;
        const margin = isPortrait
            ? LAYOUT.FROG_PAD_BOTTOM_MARGIN.PORTRAIT
            : LAYOUT.FROG_PAD_BOTTOM_MARGIN.LANDSCAPE;
        return height - padHeight / 2 - margin;
    }

    getCameraFollowOffsetY(width, height, scale) {
        const desiredFrogScreenY = this.getFrogBlockY(width, height, scale);
        return desiredFrogScreenY - (height / 2);
    }

    applyScales(scale) {
        const offsetX = OFFSETS.X * scale;
        const padSpacing = PAD_SPACING * scale;

        // Frog
        if (this.rana) {
            this.rana.setScale(SCALES.FROG_BASE * scale);
        }

        const frogX = this.scale.width / 2;
        const frogY = this.getFrogBlockY(this.scale.width, this.scale.height, scale);

        if (this.rana) {
            this.rana.setPosition(frogX, frogY);
        }

        // Frog pad
        if (this.frogPad) {
            this.frogPad.sprite.setScale(SCALES.PLATFORM_BASE * scale);
            this.frogPad.sprite.setPosition(frogX, frogY);
            this.frogPad.shadow.setScale(scale);
            this.frogPad.shadow.setPosition(frogX, frogY + OFFSETS.SHADOW * scale);
        }

        // Main columns
        if (this.columns) {
            this.columns.A.reposition(frogX - offsetX, frogY - padSpacing / 2, padSpacing, scale);
            this.columns.B.reposition(frogX, frogY - padSpacing, padSpacing, scale);
            this.columns.C.reposition(frogX + offsetX, frogY - padSpacing / 2, padSpacing, scale);
        }

        // Deco columns: reposition in place (no destroy/recreate)
        if (this.decoMap.size > 0) {
            const center = this.centerXMul;
            for (const [xMul, col] of this.decoMap) {
                const dist = xMul - center;
                const cx = frogX + dist * offsetX;
                const cy = getColumnBaseY(frogY, dist, padSpacing);
                col.reposition(cx, cy, padSpacing, scale);
            }
        }

        // Shore (sueloFinal)
        if (this.sueloFinal && this._correctKey && this.columns[this._correctKey]) {
            const shoreCol = this.columns[this._correctKey];
            this.sueloFinal.setScale(scale);
            this.sueloFinal.setPosition(shoreCol.headX, shoreCol.headY - this.lastHeight - 25);
        }

        this.updateTextPositions();
    }

    updateTextPositions() {
        const labelOffset = this.lastHeight * 0.50;
        const fontScale = this.scalePlatform;
        const answerWrapWidth = Math.max(90, 150 * fontScale);
        const answerFontSize = Math.max(18, Math.round(28 * fontScale));
        const labelFontSize = Math.max(14, Math.round(20 * fontScale));
        const badgeRadius = Math.max(14, Math.round(18 * fontScale));

        for (const key of ["A", "B", "C"]) {
            const col = this.columns[key];
            const textObj = this[`text${key}`];
            const labelObj = this[`label${key}`];
            const labelShadowObj = this[`labelShadow${key}`];
            const badgeObj = this[`labelBg${key}`];
            const badgeY = col.headY - labelOffset;

            // Check if this answer has an image
            const answer = this.currentAnswers?.[key];
            const hasImage = answer?.source && (answer.type === "image" || answer.type?.startsWith("image/"));
            const hasImageOnly = hasImage && !answer?.answer;  // imagen sin texto
            const textYOffset = hasImage ? this.lastHeight * 0.34 : -this.lastHeight * 0.02;

            if (textObj && col) {
                // Align text to top of its container when image is present
                if (hasImage) { 
                    textObj.setOrigin(0.5, 0);
                    textObj.setPosition(col.headX, col.headY + this.lastHeight * 0.16);
                    textObj.setLineSpacing(-5); // Reduce line height when image is present
                } else {
                    textObj.setOrigin(0.5, 0.5);
                    textObj.setPosition(col.headX, col.headY + textYOffset);
                    textObj.setLineSpacing(0); // Default line spacing
                }
                textObj.setScale(1);
                textObj.setFontSize(answerFontSize);
                textObj.setStroke(undefined, 0);
                textObj.setWordWrapWidth(answerWrapWidth, true);
            }
            if (labelObj && col) {
                labelObj.setPosition(col.headX, badgeY);
                labelObj.setScale(1);
                labelObj.setFontSize(labelFontSize);
                labelObj.setStroke(undefined, 0);
            }
            if (labelShadowObj && col) {
                labelShadowObj.setPosition(col.headX, badgeY + Math.max(1, Math.round(2 * fontScale)));
                labelShadowObj.setRadius(Math.max(12, badgeRadius - 1));
                labelShadowObj.setAlpha(0.35);
            }
            if (badgeObj && col) {
                badgeObj.setPosition(col.headX, badgeY);
                badgeObj.setRadius(badgeRadius);
            }

            // Reposition multimedia image if present, accounting for image+text centering
            const mm = col.headMultimedia;
            if (mm && mm.setPosition && hasImage) {
                if (hasImageOnly) {
                    // Centrar imagen en el nenúfar y guardar restricciones de tamaño para el fit
                    mm.setPosition(col.headX, col.headY);
                    mm.targetFitWidth = col.headSprite.displayWidth * 0.88;
                    mm.targetFitHeight = this.lastHeight * 1.0;
                    // Si la textura ya está cargada (resize), recalcular escala directamente
                    if (mm.alpha > 0 && mm.texture?.source?.[0]?.image) {
                        const img = mm.texture.source[0].image;
                        const scaleW = mm.targetFitWidth / img.naturalWidth;
                        const scaleH = mm.targetFitHeight / img.naturalHeight;
                        const fitScale = Math.min(scaleW, scaleH);
                        mm.setScale(fitScale);
                        mm.baseScale = fitScale;
                    }
                } else {
                    // imagen + texto: fit dinámico al 70% de la altura del pad
                    mm.targetFitWidth = col.headSprite.displayWidth * 0.88;
                    mm.targetFitHeight = this.lastHeight * 0.70;
                    mm.setPosition(col.headX, col.headY - this.lastHeight * 0.16);
                    if (mm.alpha > 0 && mm.texture?.source?.[0]?.image) {
                        const img = mm.texture.source[0].image;
                        const scaleW = mm.targetFitWidth / img.naturalWidth;
                        const scaleH = mm.targetFitHeight / img.naturalHeight;
                        const fitScale = Math.min(scaleW, scaleH);
                        mm.setScale(fitScale);
                        mm.baseScale = fitScale;
                    }
                }
            }
        }

        this.adjustAnswerFontSizes();
    }

    adjustAnswerFontSizes() {
        const answerTexts = [this.textA, this.textB, this.textC].filter(Boolean);
        if (answerTexts.length === 0) return;

        const maxTextWidth = Math.max(95, this.lastHeight * 0.9);
        const maxTextHeight = Math.max(44, this.lastHeight * 0.34);

        // Calcular el fontSize necesario para cada texto individualmente
        const fontSizes = answerTexts.map((textObj) => {
            let fontSize = parseInt(textObj.style.fontSize, 10);
            if (!Number.isFinite(fontSize)) {
                fontSize = Math.max(18, Math.round(28 * this.scalePlatform));
            }

            let iterations = 0;
            while (
                iterations < 18
                && (textObj.width > maxTextWidth || textObj.height > maxTextHeight)
                && fontSize > 15
            ) {
                fontSize -= 1;
                textObj.setFontSize(fontSize);
                iterations += 1;
            }

            return parseInt(textObj.style.fontSize, 10);
        });

        // Tomar el fontSize más pequeño (el que necesita el texto más largo)
        const unifiedFontSize = Math.min(...fontSizes);

        // Aplicar el mismo fontSize a los tres textos
        for (const textObj of answerTexts) {
            textObj.setFontSize(unifiedFontSize);
        }
    }

    /**
     * Update multimedia on all three main columns based on currentAnswers
     */
    updateColumnsMultimedia() {
        for (const key of ["A", "B", "C"]) {
            const answer = this.currentAnswers[key];
            if (!answer) continue;

            const multimedia = answer.source && answer.type
                ? { source: answer.source, type: answer.type }
                : null;

            // Update the column's head multimedia
            this.columns[key].updateHeadMultimedia(multimedia);
        }
    }

    /**
     * Destroy multimedia on all three main columns
     */
    destroyAllMultimedia() {
        for (const key of ["A", "B", "C"]) {
            this.columns[key].updateHeadMultimedia(null);
        }
    }

    // ─── Question flow ───────────────────────────────────────────────

    startQuestion() {
        if (this.currentQuestionIndex >= this.questions.length) {
            this.gameWon();
            return;
        }

        const storeIndex = this.getCurrentStoreIndex();
        if (this.onQuestionStart) {
            this.onQuestionStart(storeIndex);
        }

        const question = this.getCurrentQuestion();
        if (!question) return;

        this.setBankControl({ visible: false, offsetY: BANK.HIDDEN_OFFSET_Y });

        const indices = [0, 1, 2];
        if (this.randomizeAnswers) {
            const shuffled = shuffle(indices);
            this.answerOrder = {
                A: shuffled[0],
                B: shuffled[1],
                C: shuffled[2],
            };
        } else {
            this.answerOrder = { A: 0, B: 1, C: 2 };
        }

        const answers = question.answers || [];
        this.currentCorrectAnswers = question.correctAnswers || [];
        this.currentAnswers = {
            A: answers[this.answerOrder.A] || null,
            B: answers[this.answerOrder.B] || null,
            C: answers[this.answerOrder.C] || null,
        };

        // Update multimedia on columns
        this.updateColumnsMultimedia();

        // Position image and text correctly before animating
        this.updateTextPositions();

        // Show shore (suelo) on the penultimate question
        // (or on the first one if there is only one question)
        const shoreQuestionIndex = this.questions.length > 1
            ? this.questions.length - 2
            : 0;
        if (this.currentQuestionIndex === shoreQuestionIndex) {
            this._createShore();
        }

        this.playSound("croar", { delay: 0.2 });
        this.animateAnswersIn();
    }

    getCurrentQuestion() {
        return this.questions[this.currentQuestionIndex];
    }

    getCurrentStoreIndex() {
        return this.currentQuestionIndex;
    }

    getNextStoreIndex() {
        if (this.currentQuestionIndex + 1 < this.questions.length) {
            return this.currentQuestionIndex + 1;
        }
        return undefined;
    }

    animateAnswersIn() {
        const duration = DURATIONS.ANSWER_CASCADE;
        const startOffset = 1000;

        for (const key of ["A", "B", "C"]) {
            const col = this.columns[key];
            const answer = this.currentAnswers[key];
            // Only move text down if there's an image
            const hasImage = answer?.source && (answer.type === "image" || answer.type?.startsWith("image/"));
            const textYOffset = hasImage ? this.lastHeight * 0.34 : -this.lastHeight * 0.02;

            // Set origin based on image presence
            if (hasImage) {
                this[`text${key}`].setOrigin(0.5, 0);
                this[`text${key}`].y = col.headY + this.lastHeight * 0.16 - startOffset;
            } else {
                this[`text${key}`].setOrigin(0.5, 0.5);
                this[`text${key}`].y = col.headY + textYOffset - startOffset;
            }
            this[`label${key}`].y = col.headY - this.lastHeight * 0.50 - startOffset;
            this[`labelBg${key}`].y = col.headY - this.lastHeight * 0.50 - startOffset;
            this[`labelShadow${key}`].y = col.headY - this.lastHeight * 0.50 - startOffset;
        }

        this.textA.setText(this.formatAnswerText(this.currentAnswers.A?.answer));
        this.textB.setText(this.formatAnswerText(this.currentAnswers.B?.answer));
        this.textC.setText(this.formatAnswerText(this.currentAnswers.C?.answer));

        this.adjustAnswerFontSizes();

        this.setAnswerElementsVisible(true);

        // Cascade A → B → C
        this.tweens.add({
            targets: [this.textA, this.labelShadowA, this.labelBgA, this.labelA],
            y: `+=${startOffset}`,
            duration,
            onComplete: () => {
                this.tweens.add({
                    targets: [this.textB, this.labelShadowB, this.labelBgB, this.labelB],
                    y: `+=${startOffset}`,
                    duration,
                    onComplete: () => {
                        this.tweens.add({
                            targets: [this.textC, this.labelShadowC, this.labelBgC, this.labelC],
                            y: `+=${startOffset}`,
                            duration,
                            onComplete: () => {
                                this.enableInteraction();
                                this.setBankControl({ visible: true, offsetY: BANK.VISIBLE_OFFSET_Y });
                                this.setAnimating(false);
                            },
                        });
                    },
                });
            },
        });
    }

    truncateText(text, maxLength) {
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength - 3) + "...";
    }

    formatAnswerText(text) {
        const content = (text || "").replace(/\s+/g, " ").trim();
        return this.truncateText(content, 90);
    }

    setAnswerElementsVisible(isVisible) {
        const alpha = isVisible ? 1 : 0;
        for (const key of ["A", "B", "C"]) {
            this[`text${key}`]?.setAlpha(alpha);
            this[`labelShadow${key}`]?.setAlpha(alpha);
            this[`labelBg${key}`]?.setAlpha(alpha);
            this[`label${key}`]?.setAlpha(alpha);
        }
    }

    // ─── Interaction ─────────────────────────────────────────────────

    enableInteraction() {
        this.moving = false;

        for (const key of ["A", "B", "C"]) {
            const sprite = this.columns[key].headSprite;
            sprite.setInteractive({ useHandCursor: true });
            this.setupPlatformEvents(sprite, key);
        }
    }

    setupPlatformEvents(platform, key) {
        platform.off("pointerover");
        platform.off("pointerout");
        platform.off("pointerup");
        platform.off("pointerdown");

        const col = this.columns[key];

        // Helper to get current multimedia
        const getMultimedia = () => {
            return col.headMultimedia;
        };

        // Hover glow effect
        let hoverGlow = null;

        platform.on("pointerover", () => {
            if (this.moving) return;
            this.playSound("over", { volume: 0.5 });

            const w = platform.displayWidth;
            const h = platform.displayHeight;
            if (platform.preFX && !hoverGlow && w > 0 && h > 0) {
                hoverGlow = platform.preFX.addGlow(0xb8e986, 4, 0, false, 0.1, 16);
            }

            const multimedia = getMultimedia();
            // Zoom multimedia if exists
            if (multimedia && multimedia.setScale) {
                // Kill previous tweens to prevent stacking
                this.tweens.killTweensOf(multimedia);
                this.tweens.add({
                    targets: multimedia,
                    scale: (multimedia.baseScale || multimedia.scale) * 1.5,
                    duration: DURATIONS.HOVER_SCALE,
                });

                // Hide letter badge during hover on image (only for image answers)
                const labelBg = this[`labelBg${key}`];
                const labelShadow = this[`labelShadow${key}`];
                const label = this[`label${key}`];
                if (labelBg || labelShadow || label) {
                    this.tweens.killTweensOf([labelBg, labelShadow, label]);
                    this.tweens.add({
                        targets: [labelBg, labelShadow, label],
                        alpha: 0,
                        duration: DURATIONS.HOVER_SCALE,
                    });
                }
            }

            // Kill previous tweens to prevent stacking
            this.tweens.killTweensOf(platform);
            this.tweens.add({
                targets: platform,
                scale: SCALES.PLATFORM_BASE * this.scalePlatform + 0.03,
                duration: DURATIONS.HOVER_SCALE,
            });
        });

        platform.on("pointerout", () => {
            if (this.moving) return;

            // Remove red glow
            if (hoverGlow) {
                platform.preFX.remove(hoverGlow);
                hoverGlow = null;
            }

            const multimedia = getMultimedia();
            // Reset multimedia zoom
            if (multimedia && multimedia.setScale) {
                // Kill previous tweens to reset properly
                this.tweens.killTweensOf(multimedia);
                this.tweens.add({
                    targets: multimedia,
                    scale: multimedia.baseScale || (SCALES.PLATFORM_BASE * this.scalePlatform * 0.5),
                    duration: DURATIONS.HOVER_SCALE,
                });

                // Show letter badge again after hover ends (only for image answers)
                const labelBg = this[`labelBg${key}`];
                const labelShadow = this[`labelShadow${key}`];
                const label = this[`label${key}`];
                if (labelBg || labelShadow || label) {
                    this.tweens.killTweensOf([labelBg, labelShadow, label]);
                    this.tweens.add({
                        targets: [labelBg, labelShadow, label],
                        alpha: 1,
                        duration: DURATIONS.HOVER_SCALE,
                    });
                }
            }

            // Kill previous tweens to reset properly
            this.tweens.killTweensOf(platform);
            this.tweens.add({
                targets: platform,
                scale: SCALES.PLATFORM_BASE * this.scalePlatform,
                duration: DURATIONS.HOVER_SCALE,
            });
        });

        // Handle long press for mobile modal
        let longPressTimer = null;

        platform.on("pointerdown", () => {
            const multimedia = getMultimedia();
            if (multimedia && multimedia.type === "audio") {
                // Start long press timer for audio
                longPressTimer = this.time.delayedCall(500, () => {
                    this.showMultimediaModal(multimedia);
                });
            }
        });

        platform.on("pointerup", (pointer) => {
            if (longPressTimer) {
                this.time.removeEvent(longPressTimer);
                longPressTimer = null;
            }

            if (pointer.upTime - pointer.downTime < 500) {
                const multimedia = getMultimedia();
                // Handle multimedia playback
                if (multimedia && multimedia.type === "audio") {
                    this.playAudio(multimedia.source);
                }
                this.handleAnswer(key);
            }
        });
    }

    disableInteraction() {
        for (const key of ["A", "B", "C"]) {
            const sprite = this.columns[key]?.headSprite;
            if (sprite) {
                sprite.off("pointerover");
                sprite.off("pointerout");
                sprite.off("pointerup");
                sprite.disableInteractive();

                // Clear any hover glow effect left on the platform
                if (sprite.preFX) {
                    sprite.preFX.clear();
                }
            }
        }
    }

    // ─── Answer handling ─────────────────────────────────────────────

    handleAnswer(key) {
        if (this.moving || this.finish) return;

        this.moving = true;
        this.setAnimating(true);
        this.setBankControl({ visible: false, offsetY: BANK.HIDDEN_OFFSET_Y });
        this.disableInteraction();

        // Reset platform scales to prevent mobile hover scale from persisting
        for (const colKey of ["A", "B", "C"]) {
            const platform = this.columns[colKey]?.headSprite;
            if (platform) {
                this.tweens.killTweensOf(platform);
                platform.setScale(SCALES.PLATFORM_BASE * this.scalePlatform);
            }
        }

        const selectedAnswer = this.currentAnswers[key];
        const answerIndex = this.answerOrder[key];
        const isCorrect = selectedAnswer && this.currentCorrectAnswers.includes(answerIndex.toString());

        const col = this.columns[key];
        const targetX = col.headX;
        const targetY = col.headY;
        const angle = key === "A" ? -45 : key === "C" ? 45 : 0;

        this.playSound("salto", { delay: 0.2 });
        this.playSound("pressOk");

        // Hide answer texts and destroy multimedia
        this.setAnswerElementsVisible(false);
        this.destroyAllMultimedia();

        // For lateral jumps, pre-create the near-edge deco column BEFORE the
        // tween starts.  The camera follows the frog during the jump, so the
        // far edge scrolls into view *during* the tween.  By creating the
        // column now (and keeping it out of decoMap) it's already there when
        // the camera reveals that area.
        if (key === "A" || key === "C") {
            this._preCreateNearEdgeDeco(key);
        }

        // Animate frog jump
        this.rana.angle = angle;
        this.rana.anims.play("rana_salto");

        this.tweens.add({
            targets: this.rana,
            x: targetX,
            y: targetY,
            duration: DURATIONS.JUMP,
            onComplete: () => {
                this.rana.angle = 0;
                if (isCorrect) {
                    this.handleCorrectAnswer(key);
                } else {
                    this.handleWrongAnswer(key);
                }
            },
        });
    }

    handleCorrectAnswer(key) {
        this.playSound("saltoOk");
        this.playSound("coin");

        this.sinkWrongPlatforms(key);

        if (this.onAnswer) {
            const answerIndex = this.answerOrder[key];
            this.onAnswer({
                type: "selection",
                answer: answerIndex.toString(),
                selectedOption: key,
            }, {
                isCorrect: true,
                questionIndexAnswer: this.getCurrentStoreIndex(),
                nextQuestionIndex: this.getNextStoreIndex(),
            });
        }

        this.time.delayedCall(800, () => {
            this.advancePlatforms(key);
            this.currentQuestionIndex++;

            if (this.currentQuestionIndex >= this.questions.length) {
                this.gameWon();
            } else {
                this.startQuestion();
            }
        });
    }

    handleWrongAnswer(key) {
        this.playSound("saltoWrong");
        this.playSound("error", { delay: 0.3 });

        // Destroy the pre-created near-edge deco (answer was wrong, no advance)
        this._destroyPendingNearDeco();

        this.sinkExtraPlatformsOnWrongAnswer(key);

        if (this.onAnswer) {
            const answerIndex = this.answerOrder[key];
            this.onAnswer({
                type: "selection",
                answer: answerIndex.toString(),
                selectedOption: key,
            }, {
                isCorrect: false,
                questionIndexAnswer: this.getCurrentStoreIndex(),
                nextQuestionIndex: this.getNextStoreIndex(),
            });
        }

        this.animateSinking(key);
    }

    // ─── Correct answer: sink wrong platforms ────────────────────────

    sinkWrongPlatforms(correctKey) {
        this.playSound("hundimiento");
        const scale = this.scalePlatform;
        const center = this.centerXMul;

        // Sink frogPad (where the frog was standing)
        this._sinkPad(this.frogPad, "hundimiento_platform", scale);

        // Sink the heads of wrong-answer columns + decos on the opposite side.
        // Same-side columns/decos keep their heads: they become the new A/B/C
        // or stay as decos after _advanceLateral.
        switch (correctKey) {
            case "A":
                // Opposite side = C-side (xMul > center)
                this.columns.C.sinkHead("hundimiento_platform", scale);
                for (const [xMul, col] of this.decoMap) {
                    if (xMul > center) col.sinkHead("hundimiento_platform", scale);
                }
                break;
            case "B":
                // Both sides sink
                this.columns.A.sinkHead("hundimiento_platform", scale);
                this.columns.C.sinkHead("hundimiento_platform", scale);
                this._sinkDecoHeads(scale);
                break;
            case "C":
                // Opposite side = A-side (xMul < center)
                this.columns.A.sinkHead("hundimiento_platform", scale);
                for (const [xMul, col] of this.decoMap) {
                    if (xMul < center) col.sinkHead("hundimiento_platform", scale);
                }
                break;
        }
    }

    // ─── Wrong answer: sink extra platforms ──────────────────────────

    sinkExtraPlatformsOnWrongAnswer(wrongKey) {
        this.playSound("hundimiento");
        const scale = this.scalePlatform;
        // const center = this.centerXMul;

        // Sink frogPad (where the frog was standing before jumping)
        this._sinkPad(this.frogPad, "hundimiento_platform", scale);

        // Sink column heads that won't be reused.
        // - The wrongKey column sinks separately via animateSinking (water anim).
        // - For lateral wrong answers (A/C): B stays visible — the frog
        //   respawns on it, so only the opposite lateral column sinks.
        // - For center wrong answer (B): both A and C sink.
        // All decos sink (everything resets on wrong answer).
        switch (wrongKey) {
            case "A":
                this.columns.C.sinkHead("hundimiento_platform", scale);
                break;
            case "B":
                // B is a full padSpacing above frog (two zigzag rows),
                // so 2 pads must sink per lateral column.
                this.columns.A.sinkHeads(2, "hundimiento_platform", scale);
                this.columns.C.sinkHeads(2, "hundimiento_platform", scale);
                break;
            case "C":
                this.columns.A.sinkHead("hundimiento_platform", scale);
                break;
        }

        this._sinkDecoHeads(scale, wrongKey === "B" ? 2 : 1);
    }

    // ─── Advance platforms (correct answer) ──────────────────────────

    advancePlatforms(key) {
        const scale = this.scalePlatform;
        const padSpacing = PAD_SPACING * scale;
        const offsetX = OFFSETS.X * scale;

        // Destroy old frogPad
        this._destroyPad(this.frogPad);

        if (key === "A" || key === "C") {
            this._advanceLateral(key, padSpacing, offsetX, scale);
        } else {
            this._advanceStraight(padSpacing, scale);
        }

        // Reposition all columns to correct positions for the new frog location.
        // FIFO shifts heads up by exactly padSpacing, but during lateral jumps
        // columns change their distance from center, so the brick-wall Y offsets
        // no longer match. This call normalises every column.
        this._repositionAllColumns(scale);

        this.updateTextPositions();
    }

    /**
     * Lateral jump (A or C): shift columns, reuse via camera follow.
     * Camera follows frog → old columns naturally appear at new X positions.
     * Y positions are corrected by _repositionAllColumns after this method.
     */
    _advanceLateral(key, padSpacing, offsetX, scale) {
        const dir = key === "A" ? -1 : 1;
        const oldA = this.columns.A;
        const oldB = this.columns.B;
        const oldC = this.columns.C;
        const chosen = key === "A" ? oldA : oldC;
        const opposite = key === "A" ? oldC : oldA;

        // 1. FIFO columns with sunk heads (opposite + opposite-side decos)
        //    Same-side decos also FIFO structurally (head scrolled off screen).
        this._fifoColumn(opposite, padSpacing, scale);
        for (const [, col] of this.decoMap) {
            this._fifoColumn(col, padSpacing, scale);
        }

        // 2. Chosen column head → frogPad, maintain column with new tail
        this.frogPad = chosen.dequeueHead();
        chosen.enqueueTail(padSpacing, scale);

        // 3. Shift center
        const oldCenter = this.centerXMul;
        this.centerXMul += dir;
        const center = this.centerXMul;

        // 4. Opposite column → decoMap (it scrolled to the far side)
        this.decoMap.set(oldCenter - dir, opposite);

        // 5. Pull old deco to become the new edge main column
        const edgeXMul = center + dir;
        const edgeCol = this.decoMap.get(edgeXMul);
        if (edgeCol) this.decoMap.delete(edgeXMul);

        // 6. Assign new column roles
        //    chosen → new B, oldB → opposite side, edgeCol → new edge
        if (key === "A") {
            this.columns = { A: edgeCol, B: chosen, C: oldB };
        } else {
            this.columns = { A: oldB, B: chosen, C: edgeCol };
        }

        // 7. Destroy the deco that scrolled off the far edge
        const farXMul = key === "A" ? center + SIDE_COLUMNS + 2 : center - SIDE_COLUMNS - 2;
        if (this.decoMap.has(farXMul)) {
            this.decoMap.get(farXMul).destroy();
            this.decoMap.delete(farXMul);
        }

        // 8. Near-edge deco: adopt pre-created column if available, else create fresh
        const nearXMul = key === "A" ? center - SIDE_COLUMNS - 1 : center + SIDE_COLUMNS + 1;
        if (this._pendingNearDeco && this._pendingNearDeco.xMul === nearXMul) {
            this.decoMap.set(nearXMul, this._pendingNearDeco.col);
            this._pendingNearDeco = null;
        } else {
            this._destroyPendingNearDeco();
            const nearDist = nearXMul - center;
            const frogX = this.frogPad.sprite.x;
            const frogY = this.frogPad.sprite.y;
            this.decoMap.set(nearXMul, LilyColumn.create(
                this,
                frogX + nearDist * offsetX,
                getColumnBaseY(frogY, nearDist, padSpacing),
                padSpacing, PAD_COUNT, scale,
            ));
        }
    }

    /**
     * Straight jump (B): no shift, all columns FIFO in place.
     */
    _advanceStraight(padSpacing, scale) {
        // ColB head → frogPad
        this.frogPad = this.columns.B.dequeueHead();
        this.columns.B.enqueueTail(padSpacing, scale);

        // ColA and ColC had sunk heads → FIFO
        this._fifoColumn(this.columns.A, padSpacing, scale);
        this._fifoColumn(this.columns.C, padSpacing, scale);

        // All decos: FIFO
        for (const [, col] of this.decoMap) {
            this._fifoColumn(col, padSpacing, scale);
        }
    }

    /**
     * Reposition every column (A, B, C and decorative) to the geometrically
     * correct position derived from the current frog position and the
     * brick-wall stagger pattern.  This is a no-op when positions already
     * match (e.g. after a straight jump) and essential after lateral jumps
     * where FIFO alone leaves columns at wrong Y offsets.
     */
    _repositionAllColumns(scale) {
        const frogX = this.frogPad.sprite.x;
        const frogY = this.frogPad.sprite.y;
        const offsetX = OFFSETS.X * scale;
        const padSpacing = PAD_SPACING * scale;

        this.columns.A.reposition(frogX - offsetX, frogY - padSpacing / 2, padSpacing, scale);
        this.columns.B.reposition(frogX, frogY - padSpacing, padSpacing, scale);
        this.columns.C.reposition(frogX + offsetX, frogY - padSpacing / 2, padSpacing, scale);

        const center = this.centerXMul;
        for (const [xMul, col] of this.decoMap) {
            const dist = xMul - center;
            const cx = frogX + dist * offsetX;
            const cy = getColumnBaseY(frogY, dist, padSpacing);
            col.reposition(cx, cy, padSpacing, scale);
        }

        // Also reposition the pre-created near-edge deco if it exists
        if (this._pendingNearDeco) {
            const dist = this._pendingNearDeco.xMul - center;
            const cx = frogX + dist * offsetX;
            const cy = getColumnBaseY(frogY, dist, padSpacing);
            this._pendingNearDeco.col.reposition(cx, cy, padSpacing, scale);
        }
    }

    // ─── Wrong answer: sinking animation ─────────────────────────────

    animateSinking(key) {
        const col = this.columns[key];

        this.playSound("hundimiento", { volume: 0.3 });
        this.playSound("burbujas", { volume: 0.3, delay: 0.3 });

        // Sink the pad where frog landed (column head) with water animation
        col.head.shadow.setAlpha(0);
        col.headSprite.setScale(this.scalePlatform);
        col.headSprite.anims.play("hundimiento_agua");

        col.headSprite.once("animationcomplete", () => {
            col.headSprite.setAlpha(0);
            this.respawnFrog(key);
        });

        // Fade out frog
        this.tweens.add({
            targets: this.rana,
            alpha: 0,
            scale: 0.8 * SCALES.FROG_BASE * this.scalePlatform,
            duration: DURATIONS.SINKING,
        });
    }

    // ─── Respawn after wrong answer ──────────────────────────────────

    /**
     * @param {string|null} wrongKey - The column the frog jumped to (A/B/C),
     *   or null for timeout (frog didn't jump).
     *   - A/C: frog respawns on column B's head (which stayed visible).
     *   - B/null: frog respawns at the old frogPad position.
     */
    respawnFrog(wrongKey) {
        // If game-over was signalled by the framework (lives depleted),
        // skip respawn and play the game-over sequence instead.
        if (this._markedGameOver) {
            this.finish = true;
            this.playSound("gameOver");
            this.setAnswerElementsVisible(false);
            this._destroyShore();

            const scale = this.scalePlatform;
            // Sink any remaining platforms
            for (const col of Object.values(this.columns)) {
                col.sinkAll("hundimiento_platform", scale);
            }
            for (const col of this.decoMap.values()) {
                col.sinkAll("hundimiento_platform", scale);
            }

            // Resolve the animateExit promise after the sinking plays out
            this.time.delayedCall(DURATIONS.SINKING, () => {
                if (this._onGameOverComplete) {
                    this._onGameOverComplete();
                    this._onGameOverComplete = null;
                }
            });
            return;
        }

        let respawnX, respawnY;

        if ((wrongKey === "A" || wrongKey === "C") && this.columns.B?.headSprite) {
            // Lateral miss → respawn on B's head (the pad that stayed visible)
            respawnX = this.columns.B.headX;
            respawnY = this.columns.B.headY;
        } else {
            // Center miss or timeout → respawn at old frogPad position
            respawnX = this.frogPad?.sprite?.x ?? this.rana.x;
            respawnY = this.frogPad?.sprite?.y ?? this.rana.y;
        }

        this.recreateAllPlatforms(respawnX, respawnY);

        // Handle shore offset for penultimate question lateral miss
        const shoreQuestionIndex = this.questions.length > 1
            ? this.questions.length - 2
            : 0;
        const isLateralMiss = wrongKey === "A" || wrongKey === "C";
        if (this.currentQuestionIndex === shoreQuestionIndex && isLateralMiss && this.sueloFinal) {
            const scale = this.scalePlatform;
            const offsetY = (PAD_BASE_SIZE/2 + 25) * scale;
            const offsetX = (PAD_BASE_SIZE + 8) * scale;

            // Move shore up and to the side opposite of the wrong answer
            this.sueloFinal.y -= offsetY;
            this.sueloFinal.x += offsetX;
        }

        // Kill any active tweens on the frog (e.g., sinking scale tween)
        this.tweens.killTweensOf(this.rana);

        this.rana.setPosition(respawnX, respawnY);
        this.rana.setAlpha(0);
        this.rana.setScale(SCALES.FROG_BASE * this.scalePlatform);

        this.cameras.main.startFollow(
            this.rana,
            false,
            1,
            1,
            0,
            this.getCameraFollowOffsetY(this.scale.width, this.scale.height, this.scalePlatform),
        );

        this.tweens.add({
            targets: this.rana,
            alpha: 1,
            duration: 400,
            onComplete: () => {
                this.currentQuestionIndex++;
                if (this.currentQuestionIndex >= this.questions.length) {
                    // Place shore above frog for the victory animation
                    this._createShoreAt(respawnX, respawnY);
                    this.gameWon();
                } else {
                    this.startQuestion();
                }
            },
        });
    }

    recreateAllPlatforms(frogX, frogY) {
        // Destroy everything
        this._destroyPad(this.frogPad);
        this._destroyPendingNearDeco();
        if (this.columns) {
            Object.values(this.columns).forEach((col) => col.destroy());
        }
        this._destroyDecoColumns();

        // Recreate at specified position
        const scale = this.scalePlatform;
        const padSpacing = PAD_SPACING * scale;
        const offsetX = OFFSETS.X * scale;

        this.frogPad = this._createPad(frogX, frogY, scale);
        this.columns = {
            A: LilyColumn.create(this, frogX - offsetX, frogY - padSpacing / 2, padSpacing, PAD_COUNT, scale),
            B: LilyColumn.create(this, frogX, frogY - padSpacing, padSpacing, PAD_COUNT, scale),
            C: LilyColumn.create(this, frogX + offsetX, frogY - padSpacing / 2, padSpacing, PAD_COUNT, scale),
        };

        this._createDecoColumns(frogX, frogY);
    }

    // ─── Timeout sinking ─────────────────────────────────────────────

    animateSinkingFromCurrent() {
        this.playSound("hundimiento", { volume: 0.3 });
        this.playSound("burbujas", { volume: 0.3, delay: 0.3 });

        const scale = this.scalePlatform;

        // Sink frogPad with water animation
        if (this.frogPad) {
            this.frogPad.shadow.setAlpha(0);
            this.frogPad.sprite.setScale(scale);
            this.frogPad.sprite.anims.play("hundimiento_agua");

            this.frogPad.sprite.once("animationcomplete", () => {
                this.frogPad.sprite.setAlpha(0);
                this.respawnFrog(null);
            });
        }

        // Sink all column heads (frog didn't jump, everything resets)
        this.playSound("hundimiento");
        this.columns.A.sinkHead("hundimiento_platform", scale);
        this.columns.B.sinkHead("hundimiento_platform", scale);
        this.columns.C.sinkHead("hundimiento_platform", scale);

        // Sink deco heads
        this._sinkDecoHeads(scale);

        // Fade out frog
        this.tweens.add({
            targets: this.rana,
            alpha: 0,
            scale: 0.8 * SCALES.FROG_BASE * scale,
            duration: DURATIONS.SINKING,
        });
    }

    // ─── Game over ───────────────────────────────────────────────────

    triggerGameOver() {
        this.finish = true;
        this.setAnimating(true);
        this.setBankControl({ visible: false, offsetY: BANK.HIDDEN_OFFSET_Y });
        this.setAnswerElementsVisible(false);
        this._destroyShore();

        this.playSound("gameOver");
        this.playSound("hundimiento");
        this.playSound("burbujas", { delay: 0.3 });

        this._destroyPendingNearDeco();

        const scale = this.scalePlatform;

        // Sink frogPad
        this._sinkPad(this.frogPad, "hundimiento_platform", scale);

        // Sink all columns
        for (const col of Object.values(this.columns)) {
            col.sinkAll("hundimiento_platform", scale);
        }
        for (const col of this.decoMap.values()) {
            col.sinkAll("hundimiento_platform", scale);
        }

        // Fade out frog
        this.tweens.add({
            targets: this.rana,
            alpha: 0,
            scale: 0.8 * SCALES.FROG_BASE * scale,
            duration: DURATIONS.SINKING,
        });
    }

    // ─── Multimedia handling ──────────────────────────────────────────

    /**
     * Play audio from source
     */
    playAudio(source) {
        if (!source) return;

        // Check if audio is already loaded in Phaser
        const audioKey = `answer-audio-${source}`;
        if (this.sound.get(audioKey)) {
            this.sound.play(audioKey);
            return;
        }

        // Fallback: use HTML5 audio
        const audio = new Audio(source);
        audio.play().catch((err) => {
            console.error("Error playing audio:", err);
        });
    }

    /**
     * Show multimedia modal for long press on mobile
     */
    showMultimediaModal(multimedia) {
        if (!multimedia) return;

        // Create a simple overlay for mobile
        const isMobile = this.scale.isPortrait || this.scale.width < 768;
        if (!isMobile) return;

        if (multimedia.type === "audio") {
            // Show audio player or toast
            this.playAudio(multimedia.source);
        }
    }

    // ─── Victory ─────────────────────────────────────────────────────

    gameWon() {
        this.finish = true;
        this.setAnimating(true);
        this.setBankControl({ visible: false, offsetY: BANK.HIDDEN_OFFSET_Y });
        this.setAnswerElementsVisible(false);

        this.playSound("gameWin");
        this.playSound("croar");

        this.cameras.main.stopFollow(this.rana);

        const jumpHeight = this.lastHeight + 30;
        this.doVictoryJumps(8, jumpHeight);

        // Finish animating immediately so UI transitions to score screen
        // (victory jumps continue in background)
        this.time.delayedCall(50, () => {
            this.setAnimating(false);
        });

        // Resolve exit callback immediately to show score screen as rana jumps
        if (this._onGameOverComplete) {
            this._onGameOverComplete();
            this._onGameOverComplete = null;
        }
    }

    doVictoryJumps(count, height) {
        if (count <= 0) {
            return;
        }

        this.rana.anims.play("rana_salto");
        this.playSound("croar", { volume: 0.7 });
        this.playSound("salto", { volume: 0.7 });

        this.tweens.add({
            targets: this.rana,
            y: `-=${height}`,
            duration: DURATIONS.VICTORY_JUMP,
            onComplete: () => {
                this.doVictoryJumps(count - 1, height);
            },
        });
    }

    // ─── Public methods for React ────────────────────────────────────

    triggerTimeOut() {
        if (this.moving || this.finish) return;

        this.moving = true;
        this.setAnimating(true);
        this.setBankControl({ visible: false, offsetY: BANK.HIDDEN_OFFSET_Y });
        this.disableInteraction();

        this.animateSinkingFromCurrent();
    }

    // ─── Utilities ───────────────────────────────────────────────────

    playSound(key, config = {}) {
        if (!this.soundEnabled || !this.sounds || !this.sounds[key]) return;
        try {
            this.sounds[key].play(config);
        } catch {
            // Sound play failed, ignore
        }
    }

    setAnimating(isAnimating) {
        if (this.onAnimatingChange) {
            this.onAnimatingChange(isAnimating);
        }
    }

    setBankControl({ visible = true, offsetY = BANK.VISIBLE_OFFSET_Y } = {}) {
        if (this.onBankControl) {
            this.onBankControl({ visible, offsetY });
        }
    }

    /**
     * Called by the framework (via React) when lives reach 0.
     * Marks the game for a game-over sequence so respawnFrog can
     * handle it instead of showing the victory animation.
     * @param {Function} [onComplete] - Resolved when the game-over animation finishes.
     */
    setGameOver(onComplete) {
        this._markedGameOver = true;
        this._onGameOverComplete = onComplete || null;
        this.setBankControl({ visible: false, offsetY: BANK.HIDDEN_OFFSET_Y });

        // If the game already finished (e.g. won), resolve immediately.
        if (this.finish && onComplete) {
            onComplete();
            this._onGameOverComplete = null;
        }
    }

    // ─── Cleanup ─────────────────────────────────────────────────────

    destroy() {
        try {
            this._destroyShore();
            this.disableInteraction();
            this.setBankControl({ visible: true, offsetY: 0 });
        } catch {
            // Ignore errors during cleanup
        }

        if (this.keys) {
            try {
                for (const k of ["A", "B", "C", "LEFT", "UP", "RIGHT"]) {
                    if (this.keys[k]) this.keys[k].off("up");
                }
            } catch {
                // Ignore errors during cleanup
            }
        }
    }
}
